// Feste Plätze der Bausteine im Aufbau-Editor (Koordinaten im SVG, 1000 × 600) und das
// Zeichnen eines Busses als Schiene mit Abzweigungen.

import type { AufbauTask, Baustein, PortArt } from "./model.ts";

export const W = 1000, H = 600;
/** Sichtbarer Ausschnitt: oben bleibt Platz für Busse, die über die Bausteine geführt werden. */
export const VIEW = { y: 70, h: 520 };

export interface Box { x: number; y: number; w: number; h: number; titel: string; unter: string[] }
export interface PortPos { x: number; y: number; dir: "l" | "r" | "u"; stub: number; art: PortArt; label: string }
export interface Layout { boxes: Record<string, Box>; ports: Record<string, PortPos>; trennlinie?: number }

const STUB: Record<PortArt, number> = { adresse: 22, daten: 40, steuer: 58 };
const ROW: Record<PortArt, number> = { adresse: 220, daten: 300, steuer: 380 };
/** Umgehungsspuren oberhalb der Bausteine, je Busart eine eigene Höhe. */
const LANE: Record<PortArt, number> = { adresse: 128, daten: 110, steuer: 92 };

const HOCH = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const zellen = (n: number) => (n > 65536 // Riesenzahlen als Zweierpotenz: 2⁴⁸ statt 281.474.976.710.656
  ? `2${[...String(Math.log2(n))].map(c => HOCH[+c]).join("")}` : n.toLocaleString("de-DE"));
const groesse = (b: Baustein) => (b.woerter! > 1 ? `${zellen(b.woerter!)} Zellen × ${b.bits} Bit` : `${b.bits} Bit`);
/** Holt die CPU mehrere Speicherzellen auf einmal (x86-64), steht das als eigene Zeile darunter. */
const zugriff = (b: Baustein) => (b.datenbus ? [`${b.datenbus / b.bits!} Zellen pro Zugriff`] : []);

/** Anschlüsse an einer senkrechten Kante (A, D, S untereinander). */
function seite(out: Record<string, PortPos>, b: Baustein, ids: string[], x: number, dir: "l" | "r"): void {
  for (const id of ids) {
    const p = b.ports.find(q => q.id === id)!;
    out[`${b.id}.${id}`] = { x, y: ROW[p.art], dir, stub: STUB[p.art], art: p.art, label: p.art[0].toUpperCase() };
  }
}

/** Anschlüsse an der Oberkante (E/A-Werk: D und S nebeneinander). */
function oben(out: Record<string, PortPos>, b: Baustein, box: Box, xs: number[]): void {
  b.ports.forEach((p, i) => {
    out[`${b.id}.${p.id}`] = { x: xs[i], y: box.y, dir: "u", stub: STUB[p.art] - 18, art: p.art, label: p.art[0].toUpperCase() };
  });
}

export function layout(task: AufbauTask): Layout {
  const by = Object.fromEntries(task.bausteine.map(b => [b.id, b]));
  const boxes: Record<string, Box> = {};
  const ports: Record<string, PortPos> = {};
  const ea = by.ea;

  if (task.architektur === "von-neumann") {
    boxes.cpu = { x: 40, y: 150, w: 200, h: 300, titel: "CPU", unter: ["Steuerwerk", "Rechenwerk"] };
    boxes.speicher = { x: 760, y: 150, w: 200, h: 300, titel: "Arbeitsspeicher", unter: ["Programm + Daten", groesse(by.speicher), ...zugriff(by.speicher)] };
    seite(ports, by.cpu, ["A", "D", "S"], 240, "r");
    seite(ports, by.speicher, ["A", "D", "S"], 760, "l");
    if (ea) {
      boxes.ea = { x: 400, y: 490, w: 220, h: 80, titel: "E/A-Werk", unter: [`Ausgabe · ${groesse(ea)}`] };
      oben(ports, ea, boxes.ea, [460, 560]);
    }
    return { boxes, ports };
  }

  boxes.programm = { x: 16, y: 150, w: 184, h: 300, titel: "Programmspeicher", unter: ["Befehle", groesse(by.programm)] };
  boxes.cpu = { x: 360, y: 150, w: 240, h: 300, titel: "CPU", unter: [] };
  boxes.daten = { x: 800, y: 150, w: 180, h: 300, titel: "Datenspeicher", unter: ["Daten", groesse(by.daten)] };
  seite(ports, by.programm, ["A", "D", "S"], 200, "r");
  seite(ports, by.cpu, ["BA", "BD", "BS"], 360, "l");
  seite(ports, by.cpu, ["DA", "DD", "DS"], 600, "r");
  seite(ports, by.daten, ["A", "D", "S"], 800, "l");
  if (ea) {
    boxes.ea = { x: 620, y: 490, w: 180, h: 80, titel: "E/A-Werk", unter: [`Ausgabe · ${groesse(ea)}`] };
    oben(ports, ea, boxes.ea, [660, 740]);
  }
  return { boxes, ports, trennlinie: 480 };
}

export const stubEnd = (p: PortPos): [number, number] =>
  p.dir === "r" ? [p.x + p.stub, p.y] : p.dir === "l" ? [p.x - p.stub, p.y] : [p.x, p.y - p.stub];

export interface BusGeo { segs: [number, number, number, number][]; dots: [number, number][]; pill: [number, number] }

/**
 * Ein Bus als Schiene: waagerechte Hauptleitung auf Höhe des CPU-Anschlusses (sonst des ersten
 * seitlichen Anschlusses), jeder Anschluss mit kurzem Stummel und senkrechter Verbindung dorthin.
 * Liefe die Hauptleitung durch einen Baustein (z. B. Programmspeicher → Datenspeicher quer durch
 * die CPU), wird sie stattdessen oben über alle Bausteine geführt -- sonst sähe es so aus, als
 * hinge der Bus an einem Anschluss, an dem er gar nicht hängt.
 */
export function busGeo(refs: string[], ports: Record<string, PortPos>, bausteinArt: (ref: string) => string,
                       boxes: Box[]): BusGeo {
  const ps = refs.map(r => ports[r]);
  const ends = ps.map(stubEnd);
  const hub = refs.findIndex(r => bausteinArt(r) === "cpu");
  const side = ps.findIndex(p => p.dir !== "u");
  let ty = ends[hub >= 0 ? hub : side >= 0 ? side : 0][1];
  const xs = ends.map(e => e[0]);
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  if (boxes.some(b => ty > b.y && ty < b.y + b.h && x0 < b.x + b.w - 1 && x1 > b.x + 1)) ty = LANE[ps[0].art];
  const segs: BusGeo["segs"] = [];
  const dots: BusGeo["dots"] = [];
  ps.forEach((p, i) => {
    const [ex, ey] = ends[i];
    segs.push([p.x, p.y, ex, ey]);
    if (ey !== ty) segs.push([ex, ey, ex, ty]);
    if (ex > x0 && ex < x1) dots.push([ex, ty]); // Abzweig mitten aus der Schiene
  });
  if (x1 > x0) segs.push([x0, ty, x1, ty]);
  return { segs, dots, pill: [(x0 + x1) / 2, ty] };
}
